<script setup>
import {
  shallowRef,
  ref,
  computed,
  onMounted,
  defineAsyncComponent,
  onUnmounted,
} from 'vue';
import { useRsvpStore } from '@/stores/rsvp/rsvp.store';
import { useRoute } from 'vue-router';
import router from '@/router';
import { InputCheckboxTrueFalse } from '@/components/inputs';
import Tr from '@/i18n/translation';

const rsvpStore = useRsvpStore();
const route = useRoute();

const mainColor = rsvpStore.getColors.mainColor;
const backgroundColor = rsvpStore.getColors.backgroundColor;
const textFont =
  rsvpStore.clientData.common.textFont &&
  rsvpStore.clientData.common.textFont.trim() !== ''
    ? rsvpStore.clientData.common.textFont
    : 'null';

const rsvpFormRef = ref(null);

const additionalQuestionsComponents = shallowRef([]);

const isNewUnSavedGuest = ref(false);

const guestId = ref(null);
const name = ref('');
const surname = ref('');
const presence = ref(true);
const additionalQuestions = ref({});

const arrowColor = computed(() => {
  return `fill:${mainColor};`;
});

onMounted(() => {
  guestId.value = route.params.guestId;

  if (rsvpStore.getNumberOfGuests <= guestId.value) {
    rsvpStore.addGuest();
    isNewUnSavedGuest.value = true;
  }

  name.value = rsvpStore.guestData[guestId.value].name;
  surname.value = rsvpStore.guestData[guestId.value].surname;
  presence.value = rsvpStore.guestData[guestId.value].presence;

  additionalQuestions.value = {
    ...rsvpStore.guestData[guestId.value].additionalQuestions,
  };

  for (
    let i = 0;
    i < rsvpStore.clientData.common.additionalQuestions.length;
    ++i
  ) {
    additionalQuestionsComponents.value.push(
      dynamicImport(
        rsvpStore.clientData.common.additionalQuestions[i].inputType,
      ),
    );
  }
});

onUnmounted(() => {
  if (isNewUnSavedGuest.value) {
    rsvpStore.removeGuest(guestId.value);
  }
});

function onValidationError(ref) {
  if (ref) {
    ref.$el.scrollIntoView();
    ref.focus();
  }
}

function saveRsvp() {
  isNewUnSavedGuest.value = false;
  rsvpStore.guestData[guestId.value].name = name.value;
  rsvpStore.guestData[guestId.value].surname = surname.value;
  rsvpStore.guestData[guestId.value].presence = presence.value;
  rsvpStore.guestData[guestId.value].additionalQuestions =
    additionalQuestions.value;

  const params = route.params;
  router.push({ name: 'Rsvp', params, hash: `#rsvp-form` });
}

function dynamicImport(componentName) {
  return defineAsyncComponent(
    () => import(`../../../../../../../inputs/${componentName}.vue`),
  );
}
</script>

<template>
  <div class="guest-form">
    <div class="back">
      <router-link :to="Tr.i18nRoute({ name: 'Rsvp' })">
        <svg
          id="Capa_1"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          width="30px"
          height="30px"
          viewBox="0 0 156.199 156.199"
          xml:space="preserve"
        >
          <path
            :style="arrowColor"
            d="M102.496,45.204h-2.499v-0.012l-77.613-0.368l32.958-32.959l-8.277-8.28L0,50.65l47.882,47.889
        l8.28-8.28l-33.719-33.73l71.642,0.346v0.04h8.417c23.151,0,41.993,18.838,41.993,41.997c0,23.151-18.842,41.992-41.993,41.992H0
        v11.711h102.496c29.613,0,53.703-24.09,53.703-53.703C156.199,69.293,132.109,45.204,102.496,45.204z"
          />
        </svg>
      </router-link>
    </div>
    <q-form
      ref="rsvpFormRef"
      class="form"
      autofocus
      @validation-error="onValidationError"
      @submit.prevent="saveRsvp"
    >
      <div class="input">
        <p class="label">
          {{ $t('appCore.apps.rsvp.components.form.guest.guestData') }}
        </p>
        <q-input
          v-model="name"
          class="core-default-input-form input-form rsvp-guest-textbox"
          borderless
          lazy-rules
          :placeholder="Tr.t('appCore.apps.rsvp.components.form.guest.name')"
          :rules="[(val) => !!val || Tr.t('appCore.common.errors.notEmpty')]"
        />
        <q-input
          v-model="surname"
          class="core-default-input-form input-form rsvp-guest-textbox"
          borderless
          lazy-rules
          :placeholder="Tr.t('appCore.apps.rsvp.components.form.guest.surname')"
          :rules="[(val) => !!val || Tr.t('appCore.common.errors.notEmpty')]"
        />
      </div>
      <InputCheckboxTrueFalse
        v-model="presence"
        :option-true="Tr.t('appCore.common.yes')"
        :option-false="Tr.t('appCore.common.no')"
        :main-color="mainColor"
        :background-color="backgroundColor"
        :text-font="textFont"
      >
        <p class="label">
          {{ $t('appCore.apps.rsvp.components.form.guest.willBePresent') }}
        </p>
      </InputCheckboxTrueFalse>
      <transition name="fade" mode="out-in">
        <div
          v-show="presence && rsvpStore.isAnyAdditionalQuestions()"
          v-if="rsvpStore.getNumberOfGuests > guestId"
          class="form"
        >
          <component
            :is="additionalQuestionsComponents[i]"
            v-for="(question, i) in rsvpStore.getAdditionalQuestions().lang"
            :key="i"
            v-model="additionalQuestions[i]"
            :presence="presence"
            :core-default-val="question.default"
            :select-options="question.selectOptions"
            :option-true="question.optionTrue"
            :option-false="question.optionFalse"
            :placeholder="question.placeholder"
            :main-color="mainColor"
            :background-color="backgroundColor"
            :text-font="textFont"
          >
            <p class="label">
              {{ question.header }}
            </p>
          </component>
        </div></transition
      >
      <div class="send">
        <button class="core-default-form-button form-button" type="submit">
          {{ $t('appCore.apps.rsvp.components.form.guest.save') }}
        </button>
      </div>
    </q-form>
  </div>
</template>

<style lang="scss">
/* stylelint-disable selector-class-pattern */
.rsvp-guest-textbox .q-field__native {
  color: v-bind(mainColor) !important;
}
/* stylelint-enable selector-class-pattern */
</style>

<style lang="scss" scoped>
@import '@/assets/styleConstans';

.guest-form {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  text-align: center;

  .back {
    width: 30px;
    height: 30px;
    transition: 1s ease all;

    :hover {
      cursor: pointer;
      width: 35px;
      height: 35px;
    }
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: 15px;

    .input {
      display: flex;
      flex-direction: column;
      gap: 15px;

      .label {
        font-family: v-bind(textFont), 'Petit Formal Script', serif;
        font-style: normal;
        font-size: 1.6em;

        @media screen and (max-width: $media-phone) {
          font-size: 1.25em;
        }
      }

      .input-form {
        border: 1px solid v-bind(mainColor);
        margin-bottom: 8px;
        background-color: v-bind(backgroundColor);
      }

      .checkbox {
        border: none;
        align-items: center;
        justify-content: center;
        background-color: v-bind(backgroundColor);
        width: auto;
        margin: auto;
      }
    }

    .form-button {
      border: 2px solid v-bind(mainColor);
      background-color: v-bind(mainColor);
      color: v-bind(backgroundColor);
      font-family: Lato, sans-serif;
      font-size: 1.6em;
      font-weight: 600;
      letter-spacing: 1px;

      &:hover {
        background-color: v-bind(backgroundColor);
        color: v-bind(mainColor);
        cursor: pointer;
      }

      @media screen and (max-width: $media-phone) {
        font-size: 1.4em;
      }
    }
  }

  .input-choose {
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    padding-top: 15px;

    .input {
      gap: 0;

      .label {
        font-family: 'MADE Mirage', serif;
        font-size: 22px;
      }
    }
  }
}

.fade-enter-active {
  transition: opacity 1.5s;
}

.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter {
  opacity: 1;
}

.fade-leave-to {
  opacity: 0;
}
</style>
